import { AspectRatio, PhotoSize } from '../types/photo';
import { useAtom } from 'jotai';
import { Model } from '@acme/db';
import { atomWithStorage } from 'jotai/utils';
import { ThemeConfiguration } from './useTrainingAtom';

export const CART_STORAGE_KEY = 'shopping-cart-atom-v6';

export type ModelSummary = Pick<
  Model,
  'id' | 'name' | 'status' | 'version' | 'teamId' | 'readableName' | 'avatarUrl' | 'modelRepositoryId'
>;
export interface CartState {
  themes?: ThemeConfiguration[];
  model?: ModelSummary;
}

const cartAtom = atomWithStorage<CartState>(CART_STORAGE_KEY, {});

export const useCartAtom = () => {
  const [cart, setCart] = useAtom(cartAtom);

  const addTheme = (theme: ThemeConfiguration) => {
    setCart(prevCart => ({ ...prevCart, themes: [...(prevCart.themes || []), theme] }));
  };

  const replaceThemes = (themes: Array<ThemeConfiguration>) => {
    setCart(prevCart => ({ ...prevCart, themes }));
  };

  const removeTheme = (id: string | number) => {
    setCart(prevCart => ({
      ...prevCart,
      themes: prevCart.themes?.filter(theme => theme.id !== id)
    }));
  };

  const setModel = (model: ModelSummary) => {
    setCart(prevCart => ({ ...prevCart, model }));
  };

  const updateThemeWorkflow = (themeId: string, workflowName: string) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;
      return {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) => (index === themeIndex ? { ...theme, workflowName } : theme))
      };
    });
  };

  const updateThemePhotoQuantity = (themeId: string, photoQuantity: number) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;
      return {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) => (index === themeIndex ? { ...theme, photoQuantity } : theme))
      };
    });
  };
  const updateThemePhotoSize = (themeId: string, aspectRatio: AspectRatio, size: PhotoSize) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;

      const newState = {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) =>
          index === themeIndex ? { ...theme, photoSize: { aspectRatio, size } } : theme
        )
      };
      console.log(newState);
      return newState;
    });
  };

  const updateThemeQuantity = (themeId: string, quantity: number) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;
      return {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) => (index === themeIndex ? { ...theme, quantity } : theme))
      };
    });
  };

  const updateThemeSelectedModifiers = (
    themeId: string,
    modifiers: Record<string, Record<string, { value: string; color?: string }>>
  ) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;
      return {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) =>
          index === themeIndex
            ? {
                ...theme,
                selectedModifiers: {
                  ...(theme.selectedModifiers || {}),
                  ...modifiers
                }
              }
            : theme
        )
      };
    });
  };

  const updateBulkThemeSelectedModifiers = (
    themeId: string,
    modifiers: Record<string, Record<string, { value: string; color?: string }>>
  ) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;
      return {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) =>
          index === themeIndex ? { ...theme, selectedModifiers: { ...modifiers } } : theme
        )
      };
    });
  };

  const updateThemeGarmentImages = (
    themeId: string,
    garmentImages: { url: string; type: 'top' | 'bottom' | 'one-piece' }[]
  ) => {
    setCart(prevCart => {
      const themeIndex = prevCart.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevCart;
      return {
        ...prevCart,
        themes: prevCart?.themes?.map((theme, index) => (index === themeIndex ? { ...theme, garmentImages } : theme))
      };
    });
  };

  const totalItems = cart.themes?.reduce((acc, theme) => acc + (Number(theme.quantity) || 1), 0) || 0;
  const hasItems = totalItems > 0;

  const clearCart = () => {
    setCart({ themes: [], model: undefined });
  };

  return {
    cart,
    addTheme,
    replaceThemes,
    removeTheme,
    setModel,
    updateThemeWorkflow,
    updateThemeGarmentImages,
    updateThemePhotoSize,
    updateThemeQuantity,
    updateThemeSelectedModifiers,
    updateThemePhotoQuantity,
    updateBulkThemeSelectedModifiers,
    totalItems,
    hasItems,
    clearCart
  };
};
